import { defineStore } from 'pinia'
import { EQO_STATUSES } from '@/utils/eqo'
import apiClient from '@/utils/apiClient'

const AUTH_TOKEN_KEY = 'authToken'
const AUTH_USER_KEY = 'authUser'
const IMPERSONATION_KEY = 'impersonationData'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem(AUTH_TOKEN_KEY) || null,
    user: JSON.parse(localStorage.getItem(AUTH_USER_KEY) || 'null'),
    impersonation: JSON.parse(localStorage.getItem(IMPERSONATION_KEY) || 'null'),
  }),

  getters: {
    hasRole: (state) => (roles) => {
      const userRole = state.user?.role?.name
      if (!userRole) return false

      if (Array.isArray(roles)) {
        return roles.includes(userRole)
      }

      return userRole === roles
    },

    hasDepartment: (state) => (department) => {
      const userDepartment = state.user?.details?.department?.name
      if (!userDepartment) return false

      return userDepartment.toLowerCase() === department.toLowerCase()
    },

    can: (state) => (permission) => {
      return state.user?.role?.permissions?.some((perm) => perm.name === permission) || false
    },

    canModifyOrder: (state) => (status) => {
      const restrictedStatuses = [EQO_STATUSES.ORDER, EQO_STATUSES.WARRANTY, EQO_STATUSES.RETURN]

      if (restrictedStatuses.includes(status)) {
        return state.user?.role?.permissions?.some((p) => p.name === 'amend orders') === true
      }

      return true
    },

    canImpersonateUser: (state) => (userId) => {
      if (!state.user) return false
      if (state.impersonation?.active) return false
      if (state.user.id === userId) return false

      return state.user?.role?.permissions?.some((perm) => perm.name === 'impersonate user') || false
    },

    getId: (state) => () => state.user?.id,

    isImpersonating: (state) => !!state.impersonation?.active,

    impersonatorUser: (state) => state.impersonation?.user ?? null,

    impersonatorToken: (state) => state.impersonation?.token ?? null,
  },

  actions: {
    setAuthData(data) {
      this.token = data.token
      this.user = data.user ?? null

      localStorage.setItem(AUTH_TOKEN_KEY, data.token)

      if (data.user) {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user))
      } else {
        localStorage.removeItem(AUTH_USER_KEY)
      }
    },

    setUser(userData) {
      this.user = { ...this.user, ...userData }
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(this.user))
    },

    setImpersonationData(data) {
      this.impersonation = data
      localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(data))
    },

    clearImpersonationData() {
      this.impersonation = null
      localStorage.removeItem(IMPERSONATION_KEY)
    },

    startImpersonation(data) {
      this.setImpersonationData({
        active: true,
        impersonatorUser: this.user,
        startedAt: new Date().toISOString(),
      })

      this.setAuthData({
        token: data.token,
        user: data.user,
      })
    },

    async stopImpersonation() {
      const { data } = await apiClient.post('/users/stop-impersonation')

      if (!data?.data?.token || !data?.data?.user) {
        throw new Error('Invalid stop impersonation response.')
      }

      this.setAuthData({
        token: data.data.token,
        user: data.data.user,
      })

      this.clearImpersonationData()

      return data
    },

    clearAuthData() {
      this.token = null
      this.user = null
      this.impersonation = null

      localStorage.removeItem(AUTH_TOKEN_KEY)
      localStorage.removeItem(AUTH_USER_KEY)
      localStorage.removeItem(IMPERSONATION_KEY)
    },

    hydrate() {
      this.token = localStorage.getItem(AUTH_TOKEN_KEY) || null
      this.user = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || 'null')
      this.impersonation = JSON.parse(localStorage.getItem(IMPERSONATION_KEY) || 'null')
    },

    async fetchMe() {
      if (!this.token) return null

      if (this._fetchMePromise) return this._fetchMePromise

      this._fetchMePromise = apiClient
        .get('/users/me', { timeout: 5000 })
        .then(({ data }) => {
          this.user = data.data
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.data))
          return data
        })
        .finally(() => {
          this._fetchMePromise = null
        })

      return this._fetchMePromise
    },
  },
})
